import { useMemo } from "react";
import dayjs from "dayjs";

const WEEK_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const NTH_LABELS = { 1:"First", 2:"Second", 3:"Third", 4:"Fourth", "-1":"Last" };

/**
 * Returns the next `count` occurrence dates for the given form values.
 * Pure client-side computation — no API call.
 */
function computeOccurrences(values, count = 5) {
  const {
    recurrenceType:       type,
    recurrenceInterval:   interval = 1,
    recurrenceDaysOfWeek: daysOfWeek = [],
    recurrenceDayOfMonth: dayOfMonth,
    recurrenceNthWeekday: nth,
    recurrenceMonth:      month,
    monthlyMode,
    recurringStartDate,
    recurrenceEndType:    endType,
    recurrenceEndDate,
    recurrenceEndAfter,
  } = values;

  if (!type || !recurringStartDate) return [];

  const results = [];
  let cursor    = dayjs(recurringStartDate).startOf("day");
  const hardEnd = endType === "on" && recurrenceEndDate
    ? dayjs(recurrenceEndDate).endOf("day")
    : null;
  const maxOcc  = endType === "after" ? parseInt(recurrenceEndAfter) || 0 : Infinity;

  const seriesStart = cursor.clone();
  let attempts = 0;

  while (results.length < count && attempts < 730) {
    attempts++;
    if (hardEnd && cursor.isAfter(hardEnd)) break;
    if (results.length >= maxOcc) break;

    if (_isOccurrence(cursor, type, interval, daysOfWeek, dayOfMonth, nth, month, seriesStart, monthlyMode))
      results.push(cursor.clone());

    cursor = cursor.add(1, "day");
  }

  return results;
}

function _isOccurrence(cursor, type, interval, daysOfWeek, dayOfMonth, nth, month, start, monthlyMode) {
  switch (type) {
    case "daily":
    case "custom":
      return cursor.diff(start, "days") % interval === 0;
    case "weekly": {
      if (!daysOfWeek.includes(cursor.day())) return false;
      const weeks = Math.floor(cursor.diff(start, "days") / 7);
      return weeks % interval === 0;
    }
    case "monthly": {
      // Same precedence and end-of-month clamp as the backend calculator
      // (scheduler/nextRunCalculator.js), so the preview cannot disagree.
      const hasNth = nth?.n != null && nth?.d != null;
      if ((monthlyMode || (hasNth ? "nth" : "fixed")) === "nth")
        return hasNth && _isNthWeekday(cursor, nth.n, nth.d);
      if (dayOfMonth == null) return false;
      return cursor.date() === Math.min(dayOfMonth, cursor.daysInMonth());
    }
    case "yearly":
      return cursor.month() === month && cursor.date() === dayOfMonth;
    default:
      return false;
  }
}

function _isNthWeekday(date, n, d) {
  if (date.day() !== d) return false;
  if (n === -1) return date.clone().add(7, "days").month() !== date.month();
  let count = 0;
  let cur = date.clone().startOf("month");
  while (cur.isBefore(date) || cur.isSame(date, "day")) {
    if (cur.day() === d) count++;
    cur = cur.add(1, "day");
  }
  return count === n;
}

/**
 * React hook — returns memoized preview dates + a human-readable summary label.
 */
const useOccurrencePreview = (values, count = 5) => {
  const dates = useMemo(() => computeOccurrences(values, count), [
    values.recurrenceType,
    values.recurrenceInterval,
    JSON.stringify(values.recurrenceDaysOfWeek),
    values.recurrenceDayOfMonth,
    JSON.stringify(values.recurrenceNthWeekday),
    values.recurrenceMonth,
    values.recurringStartDate,
    values.recurrenceEndType,
    values.recurrenceEndDate,
    values.recurrenceEndAfter,
    count,
  ]);

  return dates;
};

export { useOccurrencePreview, computeOccurrences };
